import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import {
	getCacheHistoricPortfolioValuesSummary,
	setCacheHistoricPortfolioValuesSummary,
} from '~/database/redis/queries.server.ts';
import { getUpcomingCalendarByCompanyIds } from '~/database/supabase/calendar/queries.server.ts';
import { getLatestNewsByCompanyIds } from '~/database/supabase/news/queries.server.ts';
import { getUserPortfolioWithAllRelationsById } from '~/database/supabase/portfolio/queries.server.ts';
import type { PortfolioWithAllRelationsType } from '~/database/supabase/portfolio/types.ts';
import { getPortfolioChartValuesSummary } from '~/database/supabase/prices/queries.server.ts';
import type {
	CleanPortfolioValue,
	CompactPortfolioValueBreakdown,
	LegacyPortfolioValuePositionData,
	PortfolioValueBreakdown,
} from '~/database/supabase/prices/types.ts';
import { getPortfolioCashFlowTransactions } from '~/database/supabase/transaction/queries.server.ts';
import { fetchDisplayDataForTickers } from '~/integrations/yahoo/fetch-display-data-for-tickers.server.ts';
import type { UserInstrumentDisplayDataPayload } from '~/integrations/yahoo/instrument-display-data-payload.ts';
import { toUserInstrumentDisplayDataPayload } from '~/integrations/yahoo/instrument-display-data-payload.ts';
import { getHints } from '~/utils/client-hints.tsx';
import { getPortfolioInstrumentDisplayTickers } from '~/utils/instrument-display-tickers.ts';
import {
	getPortfolioPreferenceOverrides,
	getUserPreferences,
	type PortfolioPreferenceOverrides,
} from '~/utils/user-preferences-session.server.ts';
import type {
	PortfolioCalendarItem,
	PortfolioCashFlowItem,
	PortfolioCashFlowSummary,
	PortfolioDividendItem,
	PortfolioDividendsSummary,
	PortfolioInfo,
	PortfolioNewsItem,
} from '~/views/portfolio/regular/types.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';
import { measureServerTiming } from '../utils/server-timing.ts';

export interface SerializedPortfolioValue extends Omit<CleanPortfolioValue, 'position_data'> {
	readonly position_data:
		| PortfolioValueBreakdown
		| CompactPortfolioValueBreakdown
		| LegacyPortfolioValuePositionData[]
		| null;
}

export interface PortfolioPageData {
	readonly activePortfolio: PortfolioWithAllRelationsType | null;
	readonly activePortfolioId: number;
	readonly initialInstrumentDisplayData: UserInstrumentDisplayDataPayload | null;
	readonly preferenceOverrides: PortfolioPreferenceOverrides;
	readonly portfolioInfo: PortfolioInfo;
	readonly portfolioValues: SerializedPortfolioValue[] | null;
	readonly userLocale: string;
}

function getEmptyPreferenceOverrides(): PortfolioPreferenceOverrides {
	return {
		graphCardVisibility: {},
		historyDebugBreakdownVisible: false,
		tableColumnVisibility: {},
	};
}

function getEmptyPortfolioInfo(): PortfolioInfo {
	return {
		calendar: [],
		cashFlows: {
			items: [],
			summary: {
				currency: 'SEK',
				netFlows: 0,
				totalDeposits: 0,
				totalWithdrawals: 0,
				yearly: [],
				ytdNetFlows: 0,
			},
		},
		dividends: {
			items: [],
			summary: {
				currency: 'SEK',
				netDividends: 0,
				totalDividends: 0,
				totalDividendTax: 0,
				yearly: [],
				ytdNetDividends: 0,
			},
		},
		news: [],
	};
}

function getEmptyPortfolioPageData(portfolioId: number, userLocale: string): PortfolioPageData {
	return {
		activePortfolio: null,
		activePortfolioId: portfolioId,
		initialInstrumentDisplayData: null,
		portfolioInfo: getEmptyPortfolioInfo(),
		portfolioValues: null,
		preferenceOverrides: getEmptyPreferenceOverrides(),
		userLocale,
	};
}

function isRichPositionData(value: unknown): value is PortfolioValueBreakdown {
	return typeof value === 'object' && value !== null && 'version' in value && value.version === 2;
}

function isCompactPositionData(value: unknown): value is CompactPortfolioValueBreakdown {
	return typeof value === 'object' && value !== null && 'version' in value && value.version === 3;
}

function isLegacyPositionData(value: unknown): value is LegacyPortfolioValuePositionData[] {
	if (!Array.isArray(value)) {
		return false;
	}

	return value.every((entry) => typeof entry === 'object' && entry !== null && 'position_value_id' in entry);
}

function normalizePortfolioValues(
	portfolioValues: CleanPortfolioValue[] | null | undefined,
): SerializedPortfolioValue[] | null {
	return (
		portfolioValues?.map((portfolioValue) => ({
			...portfolioValue,
			position_data:
				isRichPositionData(portfolioValue.position_data) || isCompactPositionData(portfolioValue.position_data)
					? portfolioValue.position_data
					: isLegacyPositionData(portfolioValue.position_data)
						? portfolioValue.position_data
						: null,
		})) ?? null
	);
}

function getActiveCompanyMeta(
	portfolio: PortfolioWithAllRelationsType,
): Map<number, { companyName: string; ticker: string }> {
	const activeCompanyMap = new Map<number, { companyName: string; ticker: string }>();

	for (const position of portfolio.position) {
		if (!position.instrument?.company || position.size === 0) {
			continue;
		}

		const companyId = Number(position.instrument.company.id);
		if (Number.isNaN(companyId) || activeCompanyMap.has(companyId)) {
			continue;
		}

		activeCompanyMap.set(companyId, {
			companyName: position.instrument.company.name ?? position.instrument.ticker,
			ticker: position.instrument.ticker,
		});
	}

	return activeCompanyMap;
}

function normalizeDividendType(type: string | null | undefined): 'dividend' | 'dividend_tax' | null {
	const normalized = type?.toLowerCase();

	if (normalized === 'dividend') {
		return 'dividend';
	}

	if (normalized === 'dividend_tax') {
		return 'dividend_tax';
	}

	return null;
}

function toEpochSeconds(value: string): number | null {
	const epochMs = Date.parse(value);

	if (Number.isNaN(epochMs)) {
		return null;
	}

	return Math.floor(epochMs / 1000);
}

function toDayEpochSeconds(epochSeconds: number): number {
	return new Date(epochSeconds * 1000).setUTCHours(0, 0, 0, 0).valueOf() / 1000;
}

function getCashFlowEpochSeconds(cashFlow: {
	date: number | null;
	booking_date: string | null;
	created_at: string | null;
}): number | null {
	return (
		cashFlow.date ??
		(cashFlow.booking_date ? toEpochSeconds(cashFlow.booking_date) : null) ??
		(cashFlow.created_at ? toEpochSeconds(cashFlow.created_at) : null)
	);
}

function filterInternalCashFlows(
	cashFlowRows: Awaited<ReturnType<typeof getPortfolioCashFlowTransactions>>,
): Awaited<ReturnType<typeof getPortfolioCashFlowTransactions>> {
	const rowsByDay = new Map<number, typeof cashFlowRows>();

	for (const cashFlow of cashFlowRows) {
		const epoch = getCashFlowEpochSeconds(cashFlow);

		if (!epoch) {
			continue;
		}

		const dayEpoch = toDayEpochSeconds(epoch);
		const existingRows = rowsByDay.get(dayEpoch) ?? [];

		existingRows.push(cashFlow);
		rowsByDay.set(dayEpoch, existingRows);
	}

	const filteredIds = new Set<number>();

	for (const rows of rowsByDay.values()) {
		const unmatchedWithdrawals = rows.filter((row) => row.transaction_type === 'withdrawal');

		for (const deposit of rows) {
			if (deposit.transaction_type !== 'deposit') {
				continue;
			}

			const depositAmount = typeof deposit.net_amount === 'number' ? Math.abs(deposit.net_amount) : null;
			if (depositAmount === null || !deposit.broker || !deposit.broker_account_id || filteredIds.has(deposit.id)) {
				continue;
			}

			const withdrawalIndex = unmatchedWithdrawals.findIndex((withdrawal) => {
				const withdrawalAmount = typeof withdrawal.net_amount === 'number' ? Math.abs(withdrawal.net_amount) : null;

				return (
					withdrawalAmount !== null &&
					!filteredIds.has(withdrawal.id) &&
					withdrawal.broker === deposit.broker &&
					withdrawal.broker_account_id &&
					withdrawal.broker_account_id !== deposit.broker_account_id &&
					withdrawal.transaction_currency === deposit.transaction_currency &&
					Math.abs(withdrawalAmount - depositAmount) < 0.01
				);
			});

			if (withdrawalIndex === -1) {
				continue;
			}

			const [matchedWithdrawal] = unmatchedWithdrawals.splice(withdrawalIndex, 1);
			filteredIds.add(deposit.id);
			filteredIds.add(matchedWithdrawal.id);
		}
	}

	return cashFlowRows.filter((cashFlow) => !filteredIds.has(cashFlow.id));
}

async function buildPortfolioInfo(portfolio: PortfolioWithAllRelationsType): Promise<PortfolioInfo> {
	const activeCompanyMap = getActiveCompanyMeta(portfolio);
	const companyIds = Array.from(activeCompanyMap.keys());

	if (companyIds.length === 0) {
		return getEmptyPortfolioInfo();
	}

	const [newsRows, calendarRows] = await Promise.all([
		getLatestNewsByCompanyIds(companyIds, 10),
		getUpcomingCalendarByCompanyIds(companyIds, 10),
	]);
	const rawCashFlowRows = await getPortfolioCashFlowTransactions(Number(portfolio.id));
	const cashFlowRows = filterInternalCashFlows(rawCashFlowRows);

	const news: PortfolioNewsItem[] =
		newsRows?.map((row) => {
			const companyId = Number(row.company_id);
			const companyMeta = activeCompanyMap.get(companyId);
			const publishedAt = Date.parse(`${row.date}T${row.time}`);

			return {
				companyId,
				companyName: companyMeta?.companyName ?? 'Unknown company',
				dayChange: row.day_change,
				dayVolume: row.day_volume,
				description: row.description,
				href: row.href ?? null,
				id: row.id,
				publishedAt: Number.isNaN(publishedAt) ? 0 : publishedAt,
				ticker: companyMeta?.ticker ?? '-',
			};
		}) ?? [];

	const calendar: PortfolioCalendarItem[] =
		calendarRows?.map((row) => {
			const companyId = Number(row.company_id);
			const companyMeta = activeCompanyMap.get(companyId);

			return {
				companyId,
				companyName: companyMeta?.companyName ?? 'Unknown company',
				date: row.date,
				description: row.description,
				id: row.id,
				ticker: companyMeta?.ticker ?? '-',
			};
		}) ?? [];

	const dividendItems: PortfolioDividendItem[] = [];
	const cashFlowItems: PortfolioCashFlowItem[] = [];
	const currentYear = new Date().getFullYear();
	const yearlySummaryMap = new Map<number, { totalDividends: number; totalDividendTax: number }>();
	const cashFlowYearlySummaryMap = new Map<number, { totalDeposits: number; totalWithdrawals: number }>();
	let totalDividends = 0;
	let totalDividendTax = 0;
	let ytdNetDividends = 0;
	let totalDeposits = 0;
	let totalWithdrawals = 0;
	let ytdNetFlows = 0;

	for (const position of portfolio.position) {
		if (!position.instrument?.company || position.size === 0) {
			continue;
		}

		const companyId = Number(position.instrument.company.id);
		const companyName = position.instrument.company.name ?? position.instrument.ticker;
		const ticker = position.instrument.ticker;

		for (const transaction of position.transaction) {
			const transactionType = normalizeDividendType(transaction.transaction_type ?? transaction.type);

			if (!transactionType) {
				continue;
			}

			const epoch =
				transaction.date ??
				(transaction.booking_date ? toEpochSeconds(transaction.booking_date) : null) ??
				(transaction.created_at ? toEpochSeconds(transaction.created_at) : null);
			const amount = transaction.net_amount ?? null;
			const currency = transaction.transaction_currency ?? transaction.cash_currency ?? portfolio.currency ?? 'SEK';

			if (amount !== null) {
				if (transactionType === 'dividend') {
					totalDividends += amount;
				} else {
					totalDividendTax += amount;
				}

				if (epoch) {
					const transactionYear = new Date(epoch * 1000).getFullYear();
					const yearlySummary = yearlySummaryMap.get(transactionYear) ?? {
						totalDividends: 0,
						totalDividendTax: 0,
					};

					if (transactionType === 'dividend') {
						yearlySummary.totalDividends += amount;
					} else {
						yearlySummary.totalDividendTax += amount;
					}

					yearlySummaryMap.set(transactionYear, yearlySummary);

					if (transactionYear === currentYear) {
						ytdNetDividends += amount;
					}
				}
			}

			dividendItems.push({
				amount,
				companyId,
				companyName,
				currency,
				date: epoch,
				id: transaction.id,
				ticker,
				type: transactionType,
			});
		}
	}

	const sortedDividendItems = dividendItems.sort((a, b) => {
		if (!a.date && !b.date) {
			return 0;
		}

		if (!a.date) {
			return 1;
		}

		if (!b.date) {
			return -1;
		}

		return b.date - a.date;
	});

	const summary: PortfolioDividendsSummary = {
		currency: portfolio.currency ?? 'SEK',
		netDividends: totalDividends + totalDividendTax,
		totalDividends,
		totalDividendTax,
		yearly: Array.from(yearlySummaryMap.entries())
			.map(([year, values]) => ({
				netDividends: values.totalDividends + values.totalDividendTax,
				totalDividends: values.totalDividends,
				totalDividendTax: values.totalDividendTax,
				year,
			}))
			.sort((a, b) => b.year - a.year),
		ytdNetDividends,
	};

	for (const cashFlow of cashFlowRows) {
		const rawAmount = cashFlow.net_amount;
		const amount =
			rawAmount === null ? null : cashFlow.transaction_type === 'deposit' ? Math.abs(rawAmount) : -Math.abs(rawAmount);
		const epoch = getCashFlowEpochSeconds(cashFlow);
		const currency = cashFlow.transaction_currency ?? portfolio.currency ?? 'SEK';

		if (amount !== null) {
			if (amount >= 0) {
				totalDeposits += amount;
			} else {
				totalWithdrawals += amount;
			}

			if (epoch) {
				const transactionYear = new Date(epoch * 1000).getFullYear();
				const yearlySummary = cashFlowYearlySummaryMap.get(transactionYear) ?? {
					totalDeposits: 0,
					totalWithdrawals: 0,
				};

				if (amount >= 0) {
					yearlySummary.totalDeposits += amount;
				} else {
					yearlySummary.totalWithdrawals += amount;
				}

				cashFlowYearlySummaryMap.set(transactionYear, yearlySummary);

				if (transactionYear === currentYear) {
					ytdNetFlows += amount;
				}
			}
		}

		cashFlowItems.push({
			amount,
			broker: cashFlow.broker,
			brokerAccountId: cashFlow.broker_account_id,
			currency,
			date: epoch,
			id: cashFlow.id,
			type: cashFlow.transaction_type,
		});
	}

	const cashFlowSummary: PortfolioCashFlowSummary = {
		currency: portfolio.currency ?? 'SEK',
		netFlows: totalDeposits + totalWithdrawals,
		totalDeposits,
		totalWithdrawals,
		yearly: Array.from(cashFlowYearlySummaryMap.entries())
			.map(([year, values]) => ({
				netFlows: values.totalDeposits + values.totalWithdrawals,
				totalDeposits: values.totalDeposits,
				totalWithdrawals: values.totalWithdrawals,
				year,
			}))
			.sort((a, b) => b.year - a.year),
		ytdNetFlows,
	};

	return {
		calendar,
		cashFlows: {
			items: cashFlowItems.sort((a, b) => {
				if (!a.date && !b.date) {
					return 0;
				}

				if (!a.date) {
					return 1;
				}

				if (!b.date) {
					return -1;
				}

				return b.date - a.date;
			}),
			summary: cashFlowSummary,
		},
		dividends: {
			items: sortedDividendItems.slice(0, 10),
			summary,
		},
		news,
	};
}

export const getPortfolioPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.portfolio.full',
				'portfolio.info',
				'yahoo.display-data',
				'redis.portfolio-values-summary',
				'supabase.portfolio-values-summary',
			],
			relatesTo: 'portfolio.overview',
		}),
	])
	.inputValidator((data: { portfolioId: number }) => data)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await measureServerTiming({
			description: 'auth.session',
			name: 'portfolio-overview-auth',
			run: () => requireStartAuthSession({ request }),
		});
		const userLocale = getHints(request).locale;
		const userPreferences = getUserPreferences(request);
		const portfolioId = Number.isFinite(data.portfolioId) ? data.portfolioId : -1;

		if (portfolioId < 1) {
			return getEmptyPortfolioPageData(portfolioId, userLocale);
		}

		const preferenceOverrides = getPortfolioPreferenceOverrides(userPreferences, portfolioId);
		const activePortfolio = await measureServerTiming({
			description: 'supabase.portfolio.full',
			name: 'portfolio-overview-portfolio',
			run: () =>
				getUserPortfolioWithAllRelationsById({
					portfolioId,
					userId: authSession.userId,
				}),
		});

		if (!activePortfolio) {
			return getEmptyPortfolioPageData(portfolioId, userLocale);
		}

		const [portfolioInfo, initialInstrumentDisplayData] = await Promise.all([
			measureServerTiming({
				description: 'portfolio.info',
				name: 'portfolio-overview-info',
				run: () => buildPortfolioInfo(activePortfolio),
			}),
			measureServerTiming({
				description: 'yahoo.display-data',
				name: 'portfolio-overview-display-data',
				run: () =>
					fetchDisplayDataForTickers(
						getPortfolioInstrumentDisplayTickers(activePortfolio),
						authSession.accessToken,
					).then(
						(displayData) => toUserInstrumentDisplayDataPayload(displayData) satisfies UserInstrumentDisplayDataPayload,
					),
			}),
		]);
		const cachedPortfolioValues = normalizePortfolioValues(
			await measureServerTiming({
				description: 'redis.portfolio-values-summary',
				name: 'portfolio-overview-summary-cache-get',
				run: () => getCacheHistoricPortfolioValuesSummary(authSession.userId, portfolioId),
			}),
		);

		if (cachedPortfolioValues) {
			return {
				activePortfolio,
				activePortfolioId: portfolioId,
				initialInstrumentDisplayData,
				portfolioInfo,
				portfolioValues: cachedPortfolioValues,
				preferenceOverrides,
				userLocale,
			} satisfies PortfolioPageData;
		}

		const portfolioValues = normalizePortfolioValues(
			await measureServerTiming({
				description: 'supabase.portfolio-values-summary',
				name: 'portfolio-overview-summary-db',
				run: () => getPortfolioChartValuesSummary(portfolioId, { limit: null }),
			}),
		);
		if (portfolioValues) {
			await measureServerTiming({
				description: 'redis.portfolio-values-summary',
				name: 'portfolio-overview-summary-cache-set',
				run: () => setCacheHistoricPortfolioValuesSummary(authSession.userId, portfolioId, portfolioValues),
			});
		}

		return {
			activePortfolio,
			activePortfolioId: portfolioId,
			initialInstrumentDisplayData,
			portfolioInfo,
			portfolioValues,
			preferenceOverrides,
			userLocale,
		} satisfies PortfolioPageData;
	});
